import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createExamC, fetchClasses } from "../api/client";
import type { ClassSummary } from "../api/types";
import { ArrowLeft, Upload, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

type FormState = {
  exam_name: string;
  subject: string;
  grade_level: string;
  test_date: string;
  max_points: string;
  feedback_language: string;
  class_id: string;
};

const today = new Date().toISOString().slice(0, 10);

const INITIAL: FormState = {
  exam_name: "",
  subject: "",
  grade_level: "",
  test_date: today,
  max_points: "100",
  feedback_language: "Turkish",
  class_id: "",
};

export default function ExamCCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation("pages");
  const [form, setForm] = useState<FormState>(INITIAL);
  const [rubricFile, setRubricFile] = useState<File | null>(null);
  const [questionsFile, setQuestionsFile] = useState<File | null>(null);
  const [classes, setClasses] = useState<ClassSummary[]>([]);
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClasses().then(setClasses).catch(() => setClasses([]));
  }, []);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.exam_name.trim()) { setError(t("examCCreate.nameRequired")); return; }
    if (!form.test_date) { setError(t("examCCreate.dateRequired")); return; }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
    if (rubricFile) fd.append("rubric_pdf", rubricFile);
    if (questionsFile) fd.append("questions_pdf", questionsFile);

    setStatus("submitting");
    try {
      const { exam_id } = await createExamC(fd);
      navigate(`/exams-c/${exam_id}/setup`);
    } catch (err: unknown) {
      setStatus("error");
      setError(err instanceof Error ? err.message : t("examCCreate.createError"));
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-16 mb-24">
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
          {t("common.back")}
        </button>
        <div>
          <h2 className="section-title">{t("examCCreate.title")}</h2>
          <p className="text-sm text-muted mt-4">
            {t("examCCreate.subtitle")}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: 720 }}>
        {/* Exam info card */}
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-20">{t("examCCreate.infoCard")}</h3>

          <div className="flex flex-col gap-16">
            <div className="form-group">
              <label className="form-label">{t("examCCreate.nameLabel")}</label>
              <input
                type="text"
                className="form-input"
                placeholder={t("examCCreate.namePlaceholder")}
                required
                value={form.exam_name}
                onChange={(e) => update("exam_name", e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("examCCreate.subjectLabel")}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t("examCCreate.subjectPlaceholder")}
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t("examCCreate.levelLabel")}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="9"
                  value={form.grade_level}
                  onChange={(e) => update("grade_level", e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("examCCreate.dateLabel")}</label>
                <input
                  type="date"
                  className="form-input"
                  required
                  value={form.test_date}
                  onChange={(e) => update("test_date", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t("examCCreate.maxPointsLabel")}</label>
                <input
                  type="number"
                  className="form-input"
                  min={1}
                  value={form.max_points}
                  onChange={(e) => update("max_points", e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("examCCreate.feedbackLangLabel")}</label>
                <select
                  className="form-select"
                  value={form.feedback_language}
                  onChange={(e) => update("feedback_language", e.target.value)}
                >
                  <option value="Turkish">{t("profile.feedbackLanguages.Turkish")}</option>
                  <option value="English">{t("profile.feedbackLanguages.English")}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t("examCCreate.classLabel")}</label>
                <select
                  className="form-select"
                  value={form.class_id}
                  onChange={(e) => update("class_id", e.target.value)}
                >
                  <option value="">{t("examCCreate.selectClass")}</option>
                  {classes.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* PDF uploads card */}
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-20">{t("examCCreate.materialsCard")}</h3>

          <div className="flex flex-col gap-16">
            <div className="form-group">
              <label className="form-label">{t("examCCreate.rubricLabel")}</label>
              <label className="upload-zone upload-zone-compact" style={{ cursor: "pointer" }}>
                <Upload size={16} className="upload-zone-icon" />
                <span className="upload-zone-label">
                  {rubricFile ? rubricFile.name : t("examCCreate.rubricSelectLabel")}
                </span>
                <input
                  type="file"
                  accept=".pdf"
                  style={{ display: "none" }}
                  onChange={(e) => setRubricFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">{t("examCCreate.questionPaperLabel")}</label>
              <label className="upload-zone upload-zone-compact" style={{ cursor: "pointer" }}>
                <FileText size={16} className="upload-zone-icon" />
                <span className="upload-zone-label">
                  {questionsFile ? questionsFile.name : t("examCCreate.rubricSelectLabel")}
                </span>
                <input
                  type="file"
                  accept=".pdf"
                  style={{ display: "none" }}
                  onChange={(e) => setQuestionsFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div
            className="mb-24"
            style={{
              padding: "12px 16px",
              borderRadius: "var(--radius-md)",
              background: "var(--color-danger-bg, #fef2f2)",
              color: "var(--color-danger)",
              fontSize: "var(--text-sm)",
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-12">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? t("examCCreate.submitting") : t("examCCreate.submit")}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate(-1)}
          >
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
